import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import authService from './services/auth';
import { Loader2 } from 'lucide-react';
import { usePerformanceMonitoring } from './utils/performance';

// Lazy load all route components for better performance
const Landing = lazy(() => import('./pages/Landing'));
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'));
const Trending = lazy(() => import('./pages/Trending/Trending'));
const Analytics = lazy(() => import('./pages/Analytics/Analytics'));

// Loading component for suspense fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-900">
    <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
  </div>
);

// Protected Route Component - using authStore instead of manual verification
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Simply check if user is authenticated via authStore
  const isAuthenticated = authService.isAuthenticated();
  
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

function App() {
  // Initialize performance monitoring
  usePerformanceMonitoring();
  
  return (
    <Router>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trending"
            element={
              <ProtectedRoute>
                <Trending />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics/*"
            element={
              <ProtectedRoute>
                <Analytics />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;